import { TickerMark } from './TickerMark';
import { money, pctSigned, price as fmtPrice, sharesOf } from '../lib/format';
import type { FullPosition } from '../state/derive';

/* One row per position.
 *
 * Facts only: what you hold, what it trades at, what you paid, and whether you
 * are up or down on it. Whether a position is too large for its band is a
 * judgement, and judgements live where there is room to explain them — the
 * alerts and the position's own screen — not stamped on every row.
 *
 * The percentage is measured from your average cost, not from yesterday's
 * close. A position bought at 17 and trading at 28 is up, and the row says so
 * even on a red day. */
export function HoldingsList({
  positions, onOpen, toDisplay, currency, logoFor,
}: {
  positions: FullPosition[];
  onOpen: (ticker: string) => void;
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
  /** The position's own mark, when there is one. Passed in rather than read
      here so the list stays a pure rendering of what it is given. */
  logoFor?: (p: FullPosition) => string | undefined;
}) {
  return (
    <div className="rows">
      {positions.map((p) => {
        const listingCurrency = (p.quote?.currency ?? p.currency) as 'USD';
        const up = p.totalReturnPct >= 0;
        return (
          <button key={p.ticker} className="row" onClick={() => onOpen(p.ticker)}>
            <div className="row-top">
              <TickerMark ticker={p.ticker} color={p.color} logo={logoFor?.(p)} />
              <span className="row-ticker">{p.ticker}</span>

              {/* Kept only where a number would otherwise be read as something
                  it is not: a value standing in for a missing price. */}
              <span className="row-tags">
                {p.valuedAtCost ? <span className="tag" data-kind="warn">AT COST</span>
                  : p.priceMissing ? <span className="tag" data-kind="muted">NO PRICE</span> : null}
                {p.extendedPrice !== null ? (
                  <span className="tag" data-kind="muted">
                    {p.quote?.marketState === 'PRE' ? 'PRE' : 'POST'}
                  </span>
                ) : null}
              </span>

              <span className="row-right">
                <span className="row-value num">{money(toDisplay(p.marketValue), currency)}</span>
                <span className={`row-return num ${up ? 'up' : 'down'}`}>
                  {p.costBasis > 0
                    ? `${up ? '▲' : '▼'} ${pctSigned(p.totalReturnPct)}`
                    : '—'}
                </span>
              </span>
            </div>

            <div className="row-meta">
              <span className="num">{sharesOf(p.shares, p.ticker)}</span>
              <span className="num">
                {p.priceMissing ? '—' : fmtPrice(p.price, listingCurrency)}
              </span>
              <span className="num">
                {p.avgCost > 0 ? `avg ${fmtPrice(p.avgCost, p.currency as 'USD')}` : 'avg not recorded'}
              </span>
              {/* The weight used to sit here too, and four figures on one line
                  is one more than the line can carry — it wrapped on a phone and
                  the row stopped being scannable, which is the only thing a row
                  is for. It has not gone anywhere: the ring above names every
                  slice with its weight, the position's own screen leads with it
                  under the total, and a position over its ceiling still says so
                  in the alerts. What is left here is what you cannot get from
                  the ring — how many, at what, against what you paid. */}
            </div>
          </button>
        );
      })}
    </div>
  );
}
